/**
 * Prompts Service
 * 
 * Handles all operations related to the prompt library including CRUD,
 * approval workflow, and filtering.
 */

import logger from '../core/logger.js';
import { authService } from './auth-service.js';
import {
  PromptVersion,
  PromptsFilters,
  ListPromptsResponse
} from '../types/prompt-model.js';

/**
 * Base API URL (configured via environment or defaults to relative path)
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Prompts Service Class
 */
class PromptsService {
  /**
   * List prompts with optional filters and pagination
   */
  async listPrompts(
    filters: PromptsFilters = {},
    page: number = 1,
    perPage: number = 20
  ): Promise<ListPromptsResponse> {
    logger.info('prompts-service: Listing prompts', { filters, page, perPage });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'list',
          ...filters,
          page,
          perPage
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to list prompts' }));
        throw new Error(error.error || 'Failed to list prompts');
      }
      
      const data = await response.json();
      return data;
      
    } catch (error) {
      logger.error('prompts-service: Failed to list prompts', { error, filters });
      throw error;
    }
  }
  
  /**
   * Get a single prompt by ID
   */
  async getPrompt(id: string): Promise<PromptVersion> {
    logger.info('prompts-service: Getting prompt', { id });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'get',
          id
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to get prompt' }));
        throw new Error(error.error || 'Failed to get prompt');
      }
      
      const data = await response.json();
      return data.prompt;
      
    } catch (error) {
      logger.error('prompts-service: Failed to get prompt', { error, id });
      throw error;
    }
  }
  
  /**
   * Create a new prompt (draft status)
   */
  async createPrompt(
    promptData: Omit<PromptVersion, 'id' | 'status' | 'created_by' | 'created_at' | 'approved_by' | 'approved_at'>
  ): Promise<PromptVersion> {
    logger.info('prompts-service: Creating prompt', { promptData });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'create',
          ...promptData
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to create prompt' }));
        throw new Error(error.error || 'Failed to create prompt');
      }
      
      const data = await response.json();
      return data.prompt;
      
    } catch (error) {
      logger.error('prompts-service: Failed to create prompt', { error, promptData });
      throw error;
    }
  }
  
  /**
   * Update an existing prompt
   */
  async updatePrompt(
    id: string,
    updates: Partial<Pick<PromptVersion, 'title' | 'body' | 'tags' | 'language' | 'level'>>
  ): Promise<PromptVersion> {
    logger.info('prompts-service: Updating prompt', { id, updates });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'update',
          id,
          updates
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to update prompt' }));
        throw new Error(error.error || 'Failed to update prompt');
      }
      
      const data = await response.json();
      return data.prompt;
      
    } catch (error) {
      logger.error('prompts-service: Failed to update prompt', { error, id, updates });
      throw error;
    }
  }
  
  /**
   * Approve a prompt (Admin only)
   */
  async approvePrompt(id: string): Promise<PromptVersion> {
    logger.info('prompts-service: Approving prompt', { id });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'approve',
          id
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to approve prompt' }));
        throw new Error(error.error || 'Failed to approve prompt');
      }
      
      const data = await response.json();
      return data.prompt;
      
    } catch (error) {
      logger.error('prompts-service: Failed to approve prompt', { error, id });
      throw error;
    }
  }
  
  /**
   * Deprecate a prompt (Admin only)
   */
  async deprecatePrompt(id: string): Promise<PromptVersion> {
    logger.info('prompts-service: Deprecating prompt', { id });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'deprecate',
          id
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to deprecate prompt' }));
        throw new Error(error.error || 'Failed to deprecate prompt');
      }
      
      const data = await response.json();
      return data.prompt;
      
    } catch (error) {
      logger.error('prompts-service: Failed to deprecate prompt', { error, id });
      throw error;
    }
  }
  
  /**
   * Delete a prompt (Admin only, only if in draft status)
   */
  async deletePrompt(id: string): Promise<void> {
    logger.info('prompts-service: Deleting prompt', { id });
    
    try {
      const token = authService.getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const response = await fetch(`${API_BASE_URL}/prompts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'delete',
          id
        })
      });
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to delete prompt' }));
        throw new Error(error.error || 'Failed to delete prompt');
      }
      
    } catch (error) {
      logger.error('prompts-service: Failed to delete prompt', { error, id });
      throw error;
    }
  }
}

/**
 * Singleton instance
 */
export const promptsService = new PromptsService();
